import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaChartLine,
    FaCrown,
    FaDatabase,
    FaExchangeAlt,
    FaGem,
    FaHandHoldingUsd,
    FaLeaf,
    FaLock,
    FaShieldAlt,
    FaUsers,
    FaWallet
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiFlash,
    TiPointOfInterest,
    TiTick
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Dashboard = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const assetStats = [
    { value: "$1,248,590.42", label: "Total Portfolio Balance", icon: <FaWallet className="text-cyan-400" />, sub: "+12.5% this month" },
    { value: "250,400", label: "ZERO Staked", icon: <FaGem className="text-purple-400" />, sub: "7.5% Annualized Yield" },
    { value: "$12,450.00", label: "Weekly Earnings", icon: <FaHandHoldingUsd className="text-emerald-400" />, sub: "Paid out in ZERO" },
    { value: "Operational", label: "Network Status", icon: <FaShieldAlt className="text-orange-400" />, sub: "ZC-1 Node Hub Active" },
  ];

  const pulseHub = [
    {
      title: "Zero-Bank",
      desc: "Manage linked accounts, fiat bridges, and direct transfers with institution-grade security.",
      icon: <FaDatabase />,
      link: "/banking",
      color: "cyan"
    },
    {
      title: "DAO Governance",
      desc: "Active Proposal: ZC-1 Protocol Upgrade. Cast your vote and lead the ecosystem evolution.",
      icon: <FaUsers />,
      link: "/governance",
      color: "purple"
    },
    {
      title: "NFT Vault",
      desc: "Access your tokenized conservation assets. 4 Endangered Species tokens linked to your UID.",
      icon: <FaCrown />,
      link: "/tokenomics",
      color: "orange"
    }
  ];

  const recentTransactions = [
    { asset: "ZERO", op: "Stake", status: "Confirmed", value: "50,000.00", time: "2h ago", color: "purple" },
    { asset: "USDT", op: "Swap", status: "Confirmed", value: "2,000.00", time: "5h ago", color: "emerald" },
    { asset: "BTC", op: "Receive", status: "Confirmed", value: "0.124", time: "1d ago", color: "cyan" },
    { asset: "ZERO", op: "Claim Reward", status: "Confirmed", value: "1,240.00", time: "3d ago", color: "orange" }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 shadow-[0_0_80px_rgba(34,211,238,0.15)] bg-black">
          <video
            src="/videos/dashboard-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="dashboardHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Command Centre"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Real-Time Intelligence Across the Zero Ecosystem
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Asset Intelligence Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48" ref={(el) => (sectionRefs.current[0] = el)}>
           {assetStats.map((stat, index) => (
             <motion.div
               key={index}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: index * 0.1 }}
               className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:border-cyan-500/40 transition-all group relative overflow-hidden"
             >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-4xl mb-6 group-hover:scale-110 transition-transform origin-left">{stat.icon}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">{stat.label}</div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-[10px] text-emerald-400/80 font-medium flex items-center gap-2">
                   <TiFlash className="text-sm" /> {stat.sub}
                </div>
             </motion.div>
           ))}
        </div>

        {/* Ecosystem Pulse Hub */}
        <div className="grid lg:grid-cols-3 gap-8 mb-48" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="lg:col-span-3 mb-8">
            <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">Ecosystem Pulse</h3>
            <p className="text-gray-500 text-sm">Direct access to your primary network operations.</p>
          </div>
          
          {pulseHub.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5 }}
              onClick={() => navigate(item.link)}
              className="p-10 bg-white/5 border border-white/10 rounded-[3rem] hover:bg-white/[0.08] transition-all cursor-pointer group relative overflow-hidden h-full flex flex-col justify-between"
            >
               <div>
                  <div className={`w-14 h-14 rounded-2xl bg-${item.color}-500/10 flex items-center justify-center text-3xl text-${item.color}-400 mb-8 border border-white/5 group-hover:bg-${item.color}-500/20 transition-all`}>
                    {item.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors uppercase tracking-wider">{item.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8">{item.desc}</p>
               </div>
               <div className="flex items-center gap-2 text-[10px] font-bold text-cyan-400 tracking-widest uppercase">
                  Navigate To Module <TiArrowRight className="text-lg group-hover:translate-x-1 transition-transform" />
               </div>
            </motion.div>
          ))}
        </div>

        {/* Transaction History & Market Perspective */}
        <div className="grid lg:grid-cols-2 gap-12 mb-48" ref={(el) => (sectionRefs.current[2] = el)}>
          
          {/* Recent Activity Table */}
          <div className="p-12 bg-white/5 border border-white/10 rounded-[4rem] group hover:border-cyan-500/30 transition-all">
             <div className="flex items-center justify-between mb-12">
                <h4 className="text-xl font-bold text-white uppercase tracking-[0.2em] flex items-center gap-3">
                   <FaExchangeAlt className="text-cyan-400" /> Recent Activity
                </h4>
                 <GlassButton
                   variant="gray"
                   size="sm"
                   className="px-4 py-2"
                   onClick={() => {}}
                 >
                   <span className="text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">View All</span>
                 </GlassButton>
             </div>
             
             <div className="space-y-6">
                {recentTransactions.map((tx, idx) => (
                   <div key={idx} className="flex items-center justify-between p-6 rounded-3xl bg-black/40 border border-white/5 group/tx hover:bg-white/5 transition-all">
                      <div className="flex items-center gap-5">
                         <div className={`w-12 h-12 rounded-2xl bg-${tx.color}-500/10 flex items-center justify-center text-xl text-${tx.color}-400 border border-white/5`}>
                            {tx.asset === "BTC" ? <TiPointOfInterest /> : <TiFlash />}
                         </div>
                         <div>
                            <div className="font-bold text-white mb-1 tracking-wide">{tx.asset} · {tx.op}</div>
                            <div className="text-[10px] text-gray-500 font-medium uppercase">{tx.time}</div>
                         </div>
                      </div>
                      <div className="text-right">
                         <div className="font-bold text-white mb-1">{tx.value}</div>
                         <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-tighter flex items-center gap-1 justify-end">
                            <TiTick /> {tx.status}
                         </div>
                      </div>
                   </div>
                ))}
             </div>
          </div>

          {/* Performance Visualization Placeholder */}
          <div className="p-12 bg-white/5 border border-white/10 rounded-[4rem] relative overflow-hidden group hover:border-purple-500/30 transition-all flex flex-col items-center justify-center text-center">
             <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 opacity-30 group-hover:opacity-50 transition-opacity" />
             <div className="relative z-10">
                <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center text-5xl text-purple-400 mb-10 border border-white/5 shadow-[0_0_40px_rgba(168,85,247,0.1)]">
                   <FaChartLine />
                </div>
                <h4 className="text-2xl font-bold text-white mb-6 uppercase tracking-widest">Portfolio Analytics</h4>
                <p className="text-gray-500 text-sm max-w-[280px] mx-auto leading-relaxed mb-10">
                   Visualizing your trajectory across the multi-chain ecosystem. Real-time yield tracking and risk assessment in progress.
                </p>
                <div className="flex gap-4">
                   {[1,2,3,4,5,6].map(i => (
                      <div key={i} className="w-2 bg-purple-500/30 rounded-full transition-all group-hover:bg-purple-500/60" style={{ height: `${20 + Math.random() * 60}px` }} />
                   ))}
                </div>
             </div>
          </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-500/30 to-purple-500/20" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-3xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-5xl text-cyan-400 mx-auto mb-12 shadow-[0_0_50px_rgba(34,211,238,0.2)]">
              <FaExchangeAlt />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Fuel Your Next <span className="font-zentry italic text-cyan-400 text-6xl">Move</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed">
              Bridge assets from Ethereum or BSC, claim your accumulated staking rewards, 
              or explore the latest high-yield pools. The command center gives you the leverage to stay ahead.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                onClick={() => navigate('/ico')}
                variant="cyan"
                size="lg"
                className="px-8 py-6"
              >
                Stake Now <TiFlash className="text-2xl" />
              </GlassButton>
              
              <GlassButton
                onClick={() => window.open('/Whitepapers/4-Zero-Tokens Whitepaper.docx')}
                variant="gray"
                size="lg"
                className="px-8 py-6"
              >
                Market Insight <FaChartLine className="text-2xl text-cyan-400" />
              </GlassButton>
            </div>

            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-20 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaLock /> MULTI-SIG SECURE</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaLeaf /> CARBON NEUTRAL</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaExchangeAlt /> CROSS-CHAIN HUB</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Dashboard;
